import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TimelineChart from '../components/TimelineChart';
import ScreenshotModal from '../components/ScreenshotModal';
import ResponseTimeChart from '../components/ResponseTimeChart';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatDateForCountry, getCountryByCode } from '../utils/countries';
import { SquareCheckBig, SquareX } from 'lucide-react';
import './Dashboard.css';

function DomainDetails({ user, onLogout }) {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';

  const [domain, setDomain] = useState(null);
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('24h');

  const [selectedDate, setSelectedDate] = useState(null);
  const [dateRange, setDateRange] = useState([]);

  const formatDateToYYYYMMDD = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const country = getCountryByCode(user?.country || 'TR');
  const userTimezone = country?.timezone || 'Europe/Berlin';

  const generateDateRange = useCallback(() => {
    const dates = [];
    const nowInUserTZ = new Date().toLocaleString('en-US', { timeZone: userTimezone });
    const userNow = new Date(nowInUserTZ);

    for (let i = 0; i < 7; i++) {
      const date = new Date(
        userNow.getFullYear(),
        userNow.getMonth(),
        userNow.getDate() - i,
        0, 0, 0, 0
      );
      dates.push(date);
    }

    return dates;
  }, [userTimezone]);

  const fetchChecksForDate = useCallback(async (date) => {
    try {
      const dateStr = formatDateToYYYYMMDD(date);
      const country = getCountryByCode(user?.country || 'TR');
      const userTimezone = country?.timezone || 'Europe/Istanbul';

      const response = await axios.get(`/user/domains/${domainId}/checks`, {
        params: { 
          date: dateStr,
          timezone: userTimezone
        }
      });

      setChecks(response.data.checks || []);
    } catch (error) {
      console.error('Error fetching checks for date:', error);
      setChecks([]);
    }
  }, [domainId, formatDateToYYYYMMDD, user?.country]);

  const fetchDomainDetails = useCallback(async () => {
    try {
      const [domainRes, statsRes] = await Promise.all([
        axios.get(`/user/domains/${domainId}`),
        axios.get(`/user/domains/${domainId}/stats?period=${period}`)
      ]);

      setDomain(domainRes.data.domain);
      setStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching domain details:', error);
      setLoading(false);
      if (error.response?.status === 404) {
        navigate('/domains');
      }
    }
  }, [domainId, period, navigate]);

  useEffect(() => {
    if (!domainId || domainId === 'undefined') {
      navigate('/domains');
      return;
    }

    fetchDomainDetails();

    const dates = generateDateRange();
    setDateRange(dates);
    setSelectedDate(dates[0]);

  }, [domainId, fetchDomainDetails, navigate, generateDateRange]);

  useEffect(() => {
    if (selectedDate) {
      fetchChecksForDate(selectedDate);
    }
  }, [selectedDate, fetchChecksForDate]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleCheckClick = (check) => {
    setSelectedCheck(check);
  };

  const renderLayerStatus = (status) => {
    if (!status) return '-';
    return status === 'ok' 
      ? <SquareCheckBig size={18} strokeWidth={2} color="#10b981" /> 
      : <SquareX size={18} strokeWidth={2} color="#ef4444" />;
  };

  if (loading && !domain) {
    return (
      <div className="dashboard-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="dashboard-main">
          <div className="loading-spinner">{t('loading.text')}</div>
        </main>
      </div>
    );
  }

  if (!domain) {
    return (
      <div className="dashboard-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="dashboard-main">
          <div className="error-message">{t('error.domainNotFound')}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              className="back-button"
              onClick={() => navigate('/domains')}
            >
              ← {t('action.back')}
            </button>

            <div className="site-link-container">
              <h1 className="site_address">
                <a
                  href={domain.domain_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                  style={{ 
                    border: "none",
                    background: "none",
                    fontSize: "1.275rem",
                  }}  
                >
                  {domain.domain_url}
                </a>
              </h1>
            </div>

            <p>{t('checks.last24h')}</p>
            <h3 className="check-location"> 
              <span class="live-dot"></span>
              {t('check.monitoringLocation')}: {t('check.location')}
            </h3>
          </div>

          <div className="header-top-right">
            <div className="stat-ssl-domain">
              <span className="stat-subtext">DOMAIN</span>
              <p className="ssl-domain-status">
                <span className="ssl-domain-label">{t('checks.daysLeft')}: </span>
                <span className="ssl-domain-value">
                  {domain.domain_days_left ? domain.domain_days_left : t('checks.notAvailable')}
                </span>
              </p>
              <p className="ssl-domain-status">
                <span className="ssl-domain-label">{t('checks.expireAt')}: </span>
                <span className="ssl-domain-value">
                  {domain.domain_expire_at ? formatDateForCountry(domain.domain_expire_at, user?.country, {}, currentLanguage) : t('checks.notAvailable')}
                </span>
              </p>
              <p className="ssl-domain-status">
                <span className="ssl-domain-label">{t('checks.lastChecked')}: </span>
                <span className="ssl-domain-value">
                  {domain.domain_last_checked_at ? formatDateForCountry(domain.domain_last_checked_at, user?.country, {}, currentLanguage) : t('checks.notAvailable')}
                </span>
              </p>
              <p className="ssl-domain-status">
                <span className="ssl-domain-label">REGISTRAR: </span>
                <span className="ssl-domain-value">
                  {domain.domain_registrar ? domain.domain_registrar : t('checks.notAvailable')}
                </span>
              </p>
            </div>

            <div className="stat-ssl-domain">
              <span className="stat-subtext">SSL</span>
              <p className="ssl-domain-status">
                <span className="ssl-domain-label">{t('checks.daysLeft')}: </span>
                <span className="ssl-domain-value">
                  {domain.ssl_days_left ? domain.ssl_days_left : t('checks.notAvailable')}
                </span>
              </p>
              <p className="ssl-domain-status">
                <span className="ssl-domain-label">{t('checks.expireAt')}: </span>
                <span className="ssl-domain-value">
                  {domain.ssl_expire_at ? formatDateForCountry(domain.ssl_expire_at, user?.country, {}, currentLanguage) : t('checks.notAvailable')}
                </span>
              </p>
              <p className="ssl-domain-status">
                <span className="ssl-domain-label">{t('checks.lastChecked')}: </span>
                <span className="ssl-domain-value">
                  {domain.ssl_last_checked_at ? formatDateForCountry(domain.ssl_last_checked_at, user?.country, {}, currentLanguage) : t('checks.notAvailable')}
                </span>
              </p>
            </div>
          </div>
        </header>

        {/* Response Time Chart */}
        <ResponseTimeChart 
          checks={checks} 
          user={user} 
          period={period}
          selectedDate={selectedDate}
        />

        {/* Period Selector */}
        <div className="period-selector">
          <button
            className={period === '24h' ? 'active' : ''}
            onClick={() => setPeriod('24h')}
          >
            {t('period.24h')}
          </button>
          <button
            className={period === '7d' ? 'active' : ''}
            onClick={() => setPeriod('7d')}
          >
            {t('period.7d')}
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>{t('stats.totalChecks')}</h3>
              <p className="stat-number">{stats.total_checks || 0}</p>
            </div>
            <div className="stat-card success">
              <h3>{t('stats.successful')}</h3>
              <p className="stat-number">{stats.successful_checks || 0}</p>
            </div>
            <div className="stat-card danger">
              <h3>{t('stats.failed')}</h3>
              <p className="stat-number">{stats.error_checks || 0}</p>
            </div>
            <div className="stat-card info">
              <h3>{t('stats.uptime')}</h3>
              <p className="stat-number">{stats.uptime_percentage || 0}%</p>
            </div>
            <div className="stat-card">
              <h3>{t('stats.avgResponse')}</h3>
              <p className="stat-number">
                {stats.avg_response_time
                  ? Math.round(stats.avg_response_time)
                  : 0}
                ms
              </p>
            </div>
            <div className="stat-card">
              <h3>{t('stats.maxResponse')}</h3>
              <p className="stat-number">
                {stats.max_response_time
                  ? Math.round(stats.max_response_time)
                  : 0}
                ms
              </p>
            </div>
          </div>
        )}

        {/* Timeline Chart */}
        <section className="section">
          <h2>
            {t('checks.history')} 
            {selectedDate && (
              <span className="selected-date-badge">
                {selectedDate.toLocaleDateString(currentLanguage, { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            )}
          </h2>
          <p className="section-description info"><span className="timeline-dot success"></span>{t('checks.legendOne')} <span className="timeline-dot error"></span>{t('checks.legendTwo')}</p>

          <div className="date-selector-container">
            <div className="date-selector-header">
              <h3>{t('checks.selectDate')}</h3>
              <div className="user-timezone">
                Timezone: <span className="user_timezone_info">{userTimezone}</span>
              </div>
            </div>
            <div className="date-selector-scroll">
              {dateRange.map((date, index) => {
                const isSelected = selectedDate && 
                  selectedDate.toDateString() === date.toDateString();

                return (
                  <button
                    key={index}
                    className={`date-selector-btn ${isSelected ? 'active' : ''}`}
                    onClick={() => handleDateSelect(date)}
                    title={date.toLocaleDateString(currentLanguage)}
                  >
                    <span className="date-day">
                      {date.toLocaleDateString(currentLanguage, { weekday: 'short' })}
                    </span>
                    <span className="date-number">
                      {date.toLocaleDateString(currentLanguage, { day: 'numeric' })}
                    </span>
                    <span className="date-month">
                      {date.toLocaleDateString(currentLanguage, { month: 'short' })}
                    </span>
                    {index === 0 && <span className="date-today-badge">{t('checks.today')}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {checks.length > 0 ? (
            <TimelineChart checks={checks} onCheckClick={handleCheckClick} user={user} />
          ) : (
            <div className="empty-message">
              {selectedDate ? 
                  t('checks.noDataForDate', { 
                    date: selectedDate.toLocaleDateString(currentLanguage) 
                  }) : 
                  t('checks.noData')
              }
            </div>
          )}
        </section>

        {/* Recent Errors */}
        <section className="section">
          <h2>{t('errors.recent')} (10)</h2>
          {checks.filter((c) => c.status === 'error' || c.error_message?.startsWith('CF_PROTECTED')).length === 0 ? (
            <p className="success-message">
              {selectedDate ? 
                  `No errors on ${selectedDate.toLocaleDateString(currentLanguage)}` : 
                  t('errors.none')
              }
            </p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t('check.time')}</th>
                  <th scope="col">DNS</th>
                  <th scope="col">TCP</th>
                  <th scope="col">SSL</th>
                  <th scope="col">HTTP</th>
                  <th scope="col">{t('check.statusCode')}</th>
                  <th scope="col">{t('check.errorMessage')}</th>
                  <th scope="col">{t('screenshot.screenShot')}</th>
                </tr>
              </thead>
              <tbody>
                {checks
                    .filter((c) => c.status === 'error' || c.error_message?.startsWith('CF_PROTECTED'))
                    .slice(0, 10)
                    .map((check) => {
                      const isCfProtected = check.error_message?.startsWith('CF_PROTECTED');
                      return (
                      <tr key={check.id} dnaz={check.screenshot_path} className={isCfProtected ? 'row-cf-protected' : ''}>
                        <td data-label={t('check.time')}>
                          {formatDateForCountry(check.checked_at, user?.country, {}, currentLanguage)}
                        </td>
                        <td data-label="DNS">{renderLayerStatus(check.dns_status)}</td>
                        <td data-label="TCP">{renderLayerStatus(check.tcp_status)}</td>
                        <td data-label="SSL">{renderLayerStatus(check.ssl_status)}</td>
                        <td data-label="HTTP">{renderLayerStatus(check.http_status)}</td>
                        <td data-label={t('check.statusCode')}>{check.status_code || '-'}</td>
                        <td data-label={t('error.connectionError')}>
                          {isCfProtected
                            ? <span className="badge-cf-protected">{t('error.cfProtected')}</span>
                            : (check.error_message || t('error.connectionError'))
                          }
                        </td>
                        <td data-label={t('screenshot.screenShot')}>
                          {!isCfProtected && check.screenshot_path ? (
                            <button
                              className="btn-small"
                              onClick={() => setSelectedCheck(check)}
                            >
                              {t('action.show')}
                            </button>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    )})}
              </tbody>
            </table>
          )}
        </section>

        {/* Screenshot Modal */}
        {selectedCheck && (
          <ScreenshotModal
            check={selectedCheck}
            onClose={() => setSelectedCheck(null)}
            user={user}
          />
        )}
      </main>
    </div>
  );
}

export default DomainDetails;
